import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoryImage from "@/components/CategoryImage";
import Seo from "@/components/Seo";
import { ArrowRight, Leaf, Truck, ShieldCheck, HeartHandshake, Calendar, MessageCircle, Phone } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1605351720698-6cfec9eb9b5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxpbmRvb3IlMjBob3VzZSUyMHBsYW50JTIwbW9uc3RlcmElMjBmaWRkbGUlMjBsZWFmJTIwZmlnfGVufDB8fHx8MTc4OTczOTY2N3ww&ixlib=rb-4.1.0&q=85";
const LANDSCAPE_IMG = "https://images.unsplash.com/photo-1695616827909-6f147f22d40f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxsYW5kc2NhcGUlMjBkZXNpZ24lMjBnYXJkZW4lMjBjb25zdWx0YXRpb24lMjBwbGFudHN8ZW58MHx8fHwxNzg5NzM5NjY3fDA&ixlib=rb-4.1.0&q=85";

// Decorative sublines for the backend-driven home rails (`GET /home` only returns a plain title).
const SUBTITLE_BY_KEY = {
  new_arrivals: "Fresh from our nurseries",
  best_sellers: "Loved by 10,000+ Indian homes",
  indoor: "Cleaner air, calmer spaces",
  outdoor: "Colour every corner",
  office_plants: "Desk-friendly greens that thrive indoors",
  pots_planters: "Finish the look with the right pot",
  flowering_climbers: "Vertical colour for walls & trellises",
  grafted_fruit: "Homegrown fruit, faster",
  timber_plants: "Plant today, harvest for generations",
};

function sectionHref(section) {
  const title = encodeURIComponent(section.title);
  if (section.link?.type === "collection") {
    return `/shop?collection=${encodeURIComponent(section.link.value)}&title=${title}`;
  }
  return `/shop/${encodeURIComponent(section.link.value)}?title=${title}`;
}

export default function Home() {
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cats, home] = await Promise.all([api.categories(), api.home()]);
        setCategories(cats);
        setSections(home.sections);
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    if (location.hash === "#explore-collection") {
      document.getElementById("explore-collection")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  const subscribeNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return toast.error("Please enter your email");
    setSubscribing(true);
    try {
      await api.lead({ source: "Newsletter", email: newsletterEmail, name: "", phone: "" });
      toast.success("Subscribed! Watch your inbox for care guides & offers.");
      setNewsletterEmail("");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div data-testid="home-page" className="pb-16">
      <Seo
        title="Buy Indoor, Outdoor & Flowering Plants Online"
        description="Shop indoor, outdoor, flowering and fruit plants online with YourPlants.in. AI Plant Doctor, Plant Scan and Design My Space — delivered from trusted local nurseries across Assam, Meghalaya, Arunachal Pradesh and Delhi."
        path="/"
      />
      {/* HERO */}
      <section className="hero-noise">
        <div className="mx-auto max-w-7xl px-4 py-10 md:py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-in">
            <span className="chip"><Leaf size={12} className="text-[var(--yp-primary)]" /> Apka Plant, Apke Liye</span>
            <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[var(--yp-primary)]">
              Bring nature home,<br/> <em className="text-[var(--yp-accent)] not-italic font-normal">grown with care.</em>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[var(--yp-muted)] max-w-xl leading-relaxed">
              Hand-picked plants, artisan pots, expert landscaping and doorstep delivery across India. Book a nursery visit or shop 500+ healthy plants online.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/shop" data-testid="hero-cta-shop" className="btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2">
                Shop Plants <ArrowRight size={16} />
              </Link>
              <Link to="/site-visit" data-testid="hero-cta-visit" className="rounded-full border border-[var(--yp-primary)] text-[var(--yp-primary)] px-6 py-3 text-sm font-medium inline-flex items-center gap-2 hover:bg-[var(--yp-primary)] hover:text-white transition-colors">
                <Calendar size={16} /> Book Site Visit
              </Link>
              <Link to="/bulk" data-testid="hero-cta-bulk" className="rounded-full text-[var(--yp-primary)] px-4 py-3 text-sm font-semibold hover:underline">
                Request Bulk Quote →
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {[
                { icon: Truck, label: "Delivery pan India" },
                { icon: ShieldCheck, label: "Healthy Plant Guarantee" },
                { icon: HeartHandshake, label: "AI Plant Doctor" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-2 text-xs text-[var(--yp-muted)]">
                  <Icon size={16} className="text-[var(--yp-primary)] mt-0.5" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5 fade-in">
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(27,77,62,0.18)]">
                <img src={HERO_IMG} alt="Curated indoor plants" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-[var(--yp-border)] shadow-lg p-4 w-56">
                <div className="text-xs text-[var(--yp-muted)]">Trending this week</div>
                <div className="font-serif-display text-lg text-[var(--yp-primary)]">Monstera Deliciosa</div>
                <div className="text-sm">₹649 <span className="line-through text-[var(--yp-muted)] text-xs ml-1">₹899</span></div>
              </div>
              <div className="hidden md:block absolute -top-6 -right-4 bg-[var(--yp-primary)] text-white rounded-2xl p-4 w-40">
                <div className="text-xs opacity-80">Nursery visits</div>
                <div className="font-serif-display text-xl">40+ cities</div>
                <div className="text-xs opacity-80 mt-1">Free consultation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="explore-collection" className="mx-auto max-w-7xl px-4 mt-16 scroll-mt-32">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">Shop by category</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--yp-primary)]">Explore the collection</h2>
          </div>
          <Link to="/shop" className="text-sm text-[var(--yp-primary)] hover:underline">View all →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 stagger">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/shop/${c.id}`}
              data-testid={`category-tile-${c.id}`}
              className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-[var(--yp-surface-alt)]"
            >
              <CategoryImage
                src={c.image}
                alt={c.name}
                width={360}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-4">
                <div className="font-serif-display text-lg text-white">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Browse Plants */}
      <section className="mx-auto max-w-7xl px-4 mt-20">
        <div className="bg-[var(--yp-primary)] text-white rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80">500+ healthy plants</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-2">Browse Plants</h2>
            <p className="opacity-85 mt-2 max-w-md">See our full plant listing — every category, in one place.</p>
          </div>
          <Link
            to="/shop"
            data-testid="browse-plants-cta"
            className="rounded-full bg-white text-[var(--yp-primary)] px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 hover:bg-[var(--yp-accent)] transition-colors shrink-0"
          >
            Browse Plants <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Home rails — New Arrivals, Best Seller Pack, Best Indoor Plants, Outdoor Plants,
          Best Office Plants, Pot & Planters, Flowering Climbers, Grafted Fruit Plants,
          Fast-Growing Timber Plants — order/set driven entirely by GET /home. */}
      {sections.map((s) => (
        <FeaturedRow key={s.key} title={s.title} subtitle={SUBTITLE_BY_KEY[s.key]} items={s.products} href={sectionHref(s)} />
      ))}

      {/* Editorial split — Site visit */}
      <section className="mx-auto max-w-7xl px-4 mt-20">
        <div className="grid md:grid-cols-2 gap-8 bg-[var(--yp-surface-alt)] rounded-3xl overflow-hidden">
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <span className="chip w-fit"><Calendar size={12} className="text-[var(--yp-primary)]" /> Free first consultation</span>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[var(--yp-primary)] mt-4">Not sure what your space needs? Book a nursery visit.</h2>
            <p className="text-[var(--yp-muted)] mt-3 leading-relaxed">Our horticulturists visit your home, terrace, farm or office and design a plant plan tailored to sunlight, humidity and your lifestyle.</p>
            <div className="mt-6 flex gap-3">
              <Link to="/site-visit" data-testid="section-cta-visit" className="btn-primary rounded-full px-6 py-3 text-sm font-medium">Book Site Visit</Link>
              <Link to="/services" className="rounded-full border border-[var(--yp-primary)] text-[var(--yp-primary)] px-6 py-3 text-sm font-medium hover:bg-[var(--yp-primary)] hover:text-white transition-colors">Explore services</Link>
            </div>
          </div>
          <div className="aspect-[5/4] md:aspect-auto">
            <img src={LANDSCAPE_IMG} alt="Landscape consultation" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-7xl px-4 mt-20">
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">Reviews</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--yp-primary)]">Loved across India</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {[
            { name: "Priya · Mumbai", text: "My Monstera arrived taller than I expected and beautifully packed. The care card was super helpful.", rating: 5 },
            { name: "Anand · Bengaluru", text: "Booked a terrace consultation. Their designer sent a full layout in 3 days — installation was clean.", rating: 5 },
            { name: "Ritu · Pune", text: "Bought 40 plants for our office. Delivery was on time and every plant was healthy.", rating: 5 },
          ].map((t, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[var(--yp-border)] p-6 card-hover">
              <div className="flex text-[var(--yp-accent)] mb-3">{"★★★★★".slice(0, t.rating)}</div>
              <p className="text-sm text-[var(--yp-text)] leading-relaxed">"{t.text}"</p>
              <div className="mt-4 text-xs text-[var(--yp-muted)]">{t.name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter + WhatsApp support */}
      <section className="mx-auto max-w-6xl px-4 mt-20">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-[var(--yp-surface-alt)] rounded-3xl p-8 md:p-12 text-center border border-[var(--yp-border)] flex flex-col items-center justify-center">
            <div className="chip w-fit"><MessageCircle size={12} className="text-[var(--yp-primary)]" /> Weekly gardening tips</div>
            <h3 className="font-serif-display text-3xl text-[var(--yp-primary)] mt-4">Grow with us</h3>
            <p className="text-[var(--yp-muted)] mt-2">Care guides, seasonal picks & subscriber-only offers.</p>
            <form onSubmit={subscribeNewsletter} className="mt-6 flex flex-col sm:flex-row gap-2 w-full max-w-sm">
              <input
                data-testid="newsletter-email"
                type="email"
                placeholder="Your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                className="flex-1 px-4 py-3 rounded-full border border-[var(--yp-border)] bg-white outline-none focus:border-[var(--yp-primary)]"
              />
              <button data-testid="newsletter-submit" disabled={subscribing} className="btn-primary rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60">
                {subscribing ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
          </div>
          <div className="bg-[var(--yp-primary)] text-white rounded-3xl p-8 md:p-12 text-center flex flex-col items-center justify-center">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-3 py-1 text-xs w-fit"><Phone size={12} /> Need help?</div>
            <h3 className="font-serif-display text-3xl mt-4">Stuck with plant care?</h3>
            <p className="opacity-85 mt-2">We're here to help</p>
            <a
              href="https://wa.me/918011441118"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-[var(--yp-primary)] px-6 py-3 text-sm font-semibold hover:bg-[var(--yp-accent)] hover:text-[var(--yp-text)] transition-colors"
            >
              <MessageCircle size={16} /> Chat With Us (WhatsApp)
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeaturedRow({ title, subtitle, items, href = "/shop" }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 mt-20">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">{subtitle}</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--yp-primary)]">{title}</h2>
        </div>
        <Link to={href} className="text-sm text-[var(--yp-primary)] hover:underline">Shop all →</Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 stagger">
        {items.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
}
